import {
  MagnetIcon,
  ShoppingCartIcon,
  PresentationIcon,
  CalendarIcon,
  TrophyIcon,
  UsersIcon,
  PackageIcon,
  VideoIcon,
  PhoneCallIcon,
  FlameIcon,
} from 'lucide-react'

export type Objective = {
  id: string
  label: string
  description: string
  icon: any
  funnelTypeIds: string[]
}

export const OBJECTIVES: Objective[] = [
  {
    id: 'grow-audience',
    label: 'Get more followers',
    description: 'Ask for emails so you can talk to them later.',
    icon: UsersIcon,
    funnelTypeIds: ['lead'],
  },
  {
    id: 'sell-product',
    label: 'Sell stuff online',
    description: 'Make it super easy for people to buy your things.',
    icon: PackageIcon,
    funnelTypeIds: ['product'],
  },
  {
    id: 'host-webinar',
    label: 'Do a live video',
    description: 'Teach something cool on video, then offer your thing.',
    icon: VideoIcon,
    funnelTypeIds: ['presentation'],
  },
  {
    id: 'book-calls',
    label: 'Get people to call me',
    description: 'Let people pick a time to talk to you on the phone.',
    icon: PhoneCallIcon,
    funnelTypeIds: ['appointment'],
  },
  {
    id: 'build-community',
    label: 'Run a fun group',
    description:
      'Get a bunch of people together to do something fun for a few days.',
    icon: FlameIcon,
    funnelTypeIds: ['challenge'],
  },
]

export type FunnelType = {
  id: string
  name: string
  icon: any
  description: string
  bestFor: string
  strategyOverview: string
  keyPages: string[]
  bestPractices: string[]
  expectedResults: { metric: string; value: string }
  commonMistakes: string[]
}

export const FUNNEL_TYPES: FunnelType[] = [
  {
    id: 'lead',
    name: 'Get Emails',
    icon: MagnetIcon,
    description:
      'Give away something cool for free so people give you their email address.',
    bestFor: 'Anyone starting out who wants to talk to people later',
    strategyOverview:
      'People love free stuff! Give them a helpful checklist or a short guide. In exchange, they give you their email. Now you can send them messages whenever you want.',
    keyPages: ['Sign-up Page', 'Thank You Page', 'Welcome Emails'],
    bestPractices: [
      'Make sure your free thing actually helps them do something quickly.',
      'Keep it simple — just ask for their first name and email.',
      'Give them the free thing right away on the very next page.',
    ],
    expectedResults: {
      metric: 'People who say yes',
      value: 'About 1 out of 4',
    },
    commonMistakes: [
      'Asking for too much info like their phone number or address.',
      'Giving away something boring that nobody actually wants.',
    ],
  },
  {
    id: 'product',
    name: 'Sell Stuff',
    icon: ShoppingCartIcon,
    description: 'A super simple page where people can buy your thing.',
    bestFor: 'Selling digital downloads or physical items',
    strategyOverview:
      'Make buying your stuff as easy as ordering a pizza. Show them what it is, let them pay, and maybe ask if they want fries with that right before they finish.',
    keyPages: [
      'Sales Page',
      'Checkout Page',
      'Special Offer Page',
      'Order Confirmation',
    ],
    bestPractices: [
      'Add a little checkbox at the end for a quick extra thing they can buy.',
      'Make the extra offer something that goes perfectly with what they just bought.',
      'Show happy reviews from other people right next to the buy button.',
    ],
    expectedResults: {
      metric: 'People who buy the extra thing',
      value: 'About 1 out of 5',
    },
    commonMistakes: [
      'Making them click too many times just to give you money.',
      'Confusing them with too many choices at the end.',
    ],
  },
  {
    id: 'presentation',
    name: 'Do a Webinar',
    icon: PresentationIcon,
    description:
      'Talk to a group on video, teach them something, then sell your thing.',
    bestFor: 'Selling expensive things or coaching',
    strategyOverview:
      'If your thing costs a lot, people need to trust you first. Get them on a video call, teach them something super helpful for 20 minutes, and then tell them how they can pay you to help them more.',
    keyPages: [
      'Sign-up Page',
      'Welcome Page',
      'Live Video Room',
      'Replay Page',
    ],
    bestPractices: [
      'Spend the first part just being helpful and teaching them something new.',
      'Talk to people in the chat so it feels like a real conversation.',
      'Give them a special bonus if they buy before the video is over.',
    ],
    expectedResults: {
      metric: 'People who actually show up',
      value: 'About 1 out of 3',
    },
    commonMistakes: [
      'Being boring and just reading slides the whole time.',
      'Putting way too many words on the screen.',
    ],
  },
  {
    id: 'appointment',
    name: 'Book Calls',
    icon: CalendarIcon,
    description:
      'Let people pick a time to talk to you, but only if they are a good fit.',
    bestFor: 'Coaches, consultants, and people who do services for others',
    strategyOverview:
      "You don't want to talk to just anyone. Have them answer a few quick questions first to make sure they can afford you, then let them pick a time on your calendar.",
    keyPages: [
      'Intro Video Page',
      'Questionnaire',
      'Calendar Page',
      'Get Ready Page',
    ],
    bestPractices: [
      'Record a quick video on your phone saying hi and explaining how you help.',
      'Ask a few questions to make sure they are serious.',
      "Send them a text reminder so they don't forget to show up.",
    ],
    expectedResults: {
      metric: 'People who pick a time',
      value: 'Most of them',
    },
    commonMistakes: [
      'Letting anyone book a call without asking them questions first.',
      'Not telling them what you are going to talk about on the call.',
    ],
  },
  {
    id: 'challenge',
    name: 'Run a Challenge',
    icon: TrophyIcon,
    description:
      'Get a group together to do something fun for a few days, then offer your main thing.',
    bestFor: 'Teachers, fitness coaches, and building a fun group',
    strategyOverview:
      'People love doing things together. Give them a small task to do every day for 5 days. When they see they can actually do it with your help, they will want to buy your big thing to keep going.',
    keyPages: [
      'Sign-up Page',
      'VIP Upgrade Page',
      'Daily Video Pages',
      'Final Offer Page',
    ],
    bestPractices: [
      'Ask if they want to pay a little extra for "VIP Access" right after they join.',
      'Keep the daily homework super easy — like 10 minutes max.',
      'Have everyone talk in a group chat so it feels like a party.',
    ],
    expectedResults: {
      metric: 'People who buy the VIP ticket',
      value: 'About 1 out of 10',
    },
    commonMistakes: [
      'Giving them way too much work to do every day.',
      'Waiting until the very last minute to tell them you have something for sale.',
    ],
  },
]

export type Template = {
  id: string
  title: string
  description: string
  typeId: string
  category: string
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  bestForTag: string
  stepCount: number
  strategyNote: string
}

export const TEMPLATES: Template[] = [
  // === LEAD / GET EMAILS (6 templates) ===
  {
    id: 'l1',
    title: 'Simple Opt-In',
    description: 'One page, one form. Give away a freebie, collect emails.',
    typeId: 'lead',
    category: 'Teaching & Courses',
    difficulty: 'Beginner',
    bestForTag: 'Great first funnel',
    stepCount: 2,
    strategyNote: 'Keep the headline benefit-focused.',
  },
  {
    id: 'l2',
    title: 'Newsletter Signup',
    description: 'Clean page to grow your weekly email list.',
    typeId: 'lead',
    category: 'Software & Apps',
    difficulty: 'Beginner',
    bestForTag: 'Perfect for writers',
    stepCount: 2,
    strategyNote: 'Show a preview of your best issue.',
  },
  {
    id: 'l3',
    title: 'Free PDF Download',
    description: 'Trade a helpful guide for an email address.',
    typeId: 'lead',
    category: 'Health & Fitness',
    difficulty: 'Beginner',
    bestForTag: 'Quick to set up',
    stepCount: 2,
    strategyNote: 'Make the PDF title irresistible.',
  },
  {
    id: 'l4',
    title: 'Quiz Lead Magnet',
    description: 'Fun quiz that captures emails before showing results.',
    typeId: 'lead',
    category: 'Services',
    difficulty: 'Intermediate',
    bestForTag: 'High conversion',
    stepCount: 4,
    strategyNote: 'Keep the quiz under 5 questions.',
  },
  {
    id: 'l5',
    title: 'Waitlist Page',
    description: 'Build hype before launch by collecting early signups.',
    typeId: 'lead',
    category: 'Software & Apps',
    difficulty: 'Beginner',
    bestForTag: 'Pre-launch',
    stepCount: 2,
    strategyNote: 'Add a countdown timer for urgency.',
  },
  {
    id: 'l6',
    title: 'Free Mini-Course',
    description: 'Multi-day email series that teaches something valuable.',
    typeId: 'lead',
    category: 'Teaching & Courses',
    difficulty: 'Intermediate',
    bestForTag: 'Builds trust fast',
    stepCount: 3,
    strategyNote: 'Keep each lesson under 5 minutes.',
  },

  // === PRODUCT / SELL STUFF (6 templates) ===
  {
    id: 'p1',
    title: 'Digital Product Sale',
    description: 'Sell an ebook, template, or course with a simple checkout.',
    typeId: 'product',
    category: 'Teaching & Courses',
    difficulty: 'Intermediate',
    bestForTag: 'Great for teachers',
    stepCount: 4,
    strategyNote: 'Add a bump offer on the checkout page.',
  },
  {
    id: 'p2',
    title: 'Physical Product Page',
    description: 'Showcase and sell a physical item with upsells.',
    typeId: 'product',
    category: 'Online Store',
    difficulty: 'Advanced',
    bestForTag: 'E-commerce ready',
    stepCount: 5,
    strategyNote: 'Offer bundle discounts.',
  },
  {
    id: 'p3',
    title: 'Free + Shipping',
    description: 'Give away a product for free, just charge shipping.',
    typeId: 'product',
    category: 'Online Store',
    difficulty: 'Intermediate',
    bestForTag: 'Perfect for authors',
    stepCount: 4,
    strategyNote: 'The real profit is in the upsell.',
  },
  {
    id: 'p4',
    title: 'Tripwire Offer',
    description: 'Low-price impulse buy that leads to bigger purchases.',
    typeId: 'product',
    category: 'Software & Apps',
    difficulty: 'Intermediate',
    bestForTag: 'Converts browsers to buyers',
    stepCount: 3,
    strategyNote: 'Price it under $10 for max conversions.',
  },
  {
    id: 'p5',
    title: 'Subscription Box',
    description: 'Recurring product signup with a compelling first-box offer.',
    typeId: 'product',
    category: 'Online Store',
    difficulty: 'Advanced',
    bestForTag: 'Recurring revenue',
    stepCount: 4,
    strategyNote: 'Highlight what makes the first box special.',
  },
  {
    id: 'p6',
    title: 'Flash Sale',
    description: 'Limited-time discount page with countdown timer.',
    typeId: 'product',
    category: 'Health & Fitness',
    difficulty: 'Beginner',
    bestForTag: 'Creates urgency',
    stepCount: 2,
    strategyNote: 'Use a real deadline, not a fake one.',
  },

  // === PRESENTATION / WEBINAR (5 templates) ===
  {
    id: 'w1',
    title: 'Live Webinar',
    description:
      'Registration page + live room + replay for your presentation.',
    typeId: 'presentation',
    category: 'Services',
    difficulty: 'Intermediate',
    bestForTag: 'Sells high-ticket',
    stepCount: 4,
    strategyNote: 'Teach for 20 min, pitch for 10.',
  },
  {
    id: 'w2',
    title: 'Automated Webinar',
    description: 'Pre-recorded webinar that runs on autopilot.',
    typeId: 'presentation',
    category: 'Teaching & Courses',
    difficulty: 'Advanced',
    bestForTag: 'Passive income',
    stepCount: 4,
    strategyNote: 'Make it feel live with chat simulation.',
  },
  {
    id: 'w3',
    title: 'Video Sales Letter',
    description: 'One video that does all the selling for you.',
    typeId: 'presentation',
    category: 'Health & Fitness',
    difficulty: 'Advanced',
    bestForTag: 'Great for health offers',
    stepCount: 3,
    strategyNote: 'Hook them in the first 5 seconds.',
  },
  {
    id: 'w4',
    title: 'Workshop Registration',
    description: 'Sign people up for a free training workshop.',
    typeId: 'presentation',
    category: 'Teaching & Courses',
    difficulty: 'Beginner',
    bestForTag: 'Builds authority',
    stepCount: 3,
    strategyNote: 'Promise a specific outcome in the title.',
  },
  {
    id: 'w5',
    title: 'Masterclass Invite',
    description: 'Premium-feeling invite page for an exclusive online class.',
    typeId: 'presentation',
    category: 'Services',
    difficulty: 'Intermediate',
    bestForTag: 'Premium positioning',
    stepCount: 3,
    strategyNote: 'Use words like "exclusive" and "limited seats."',
  },

  // === APPOINTMENT / BOOK CALLS (5 templates) ===
  {
    id: 'a1',
    title: 'Discovery Call',
    description: 'Qualify leads with a short form, then let them book a call.',
    typeId: 'appointment',
    category: 'Services',
    difficulty: 'Intermediate',
    bestForTag: 'Perfect for consultants',
    stepCount: 3,
    strategyNote: 'Ask about budget in the form.',
  },
  {
    id: 'a2',
    title: 'Free Consultation',
    description: 'Offer a free strategy session to attract serious leads.',
    typeId: 'appointment',
    category: 'Local Services',
    difficulty: 'Beginner',
    bestForTag: 'Great for local biz',
    stepCount: 2,
    strategyNote: 'Record a short intro video.',
  },
  {
    id: 'a3',
    title: 'Coaching Application',
    description: 'Application form that screens clients before they book.',
    typeId: 'appointment',
    category: 'Health & Fitness',
    difficulty: 'Intermediate',
    bestForTag: 'Filters tire-kickers',
    stepCount: 4,
    strategyNote: 'Only show the calendar to qualified applicants.',
  },
  {
    id: 'a4',
    title: 'Demo Request',
    description: 'Let prospects request a product demo with your team.',
    typeId: 'appointment',
    category: 'Software & Apps',
    difficulty: 'Beginner',
    bestForTag: 'B2B friendly',
    stepCount: 2,
    strategyNote: 'Follow up within 24 hours.',
  },
  {
    id: 'a5',
    title: 'Strategy Session',
    description: 'Video intro + questionnaire + calendar booking flow.',
    typeId: 'appointment',
    category: 'Services',
    difficulty: 'Advanced',
    bestForTag: 'High-ticket services',
    stepCount: 4,
    strategyNote: 'Send a reminder the day before.',
  },

  // === CHALLENGE (5 templates) ===
  {
    id: 'c1',
    title: '5-Day Challenge',
    description: 'Daily tasks for 5 days with a VIP upgrade option.',
    typeId: 'challenge',
    category: 'Health & Fitness',
    difficulty: 'Advanced',
    bestForTag: 'Builds community',
    stepCount: 5,
    strategyNote: 'Offer VIP right after signup.',
  },
  {
    id: 'c2',
    title: '3-Day Bootcamp',
    description: 'Intensive 3-day event with daily live sessions.',
    typeId: 'challenge',
    category: 'Teaching & Courses',
    difficulty: 'Intermediate',
    bestForTag: 'Fast results',
    stepCount: 4,
    strategyNote: 'Keep daily homework under 15 minutes.',
  },
  {
    id: 'c3',
    title: '7-Day Kickstart',
    description: 'Week-long guided program with email + video combo.',
    typeId: 'challenge',
    category: 'Health & Fitness',
    difficulty: 'Advanced',
    bestForTag: 'Great for fitness',
    stepCount: 5,
    strategyNote: 'Use a private group for accountability.',
  },
  {
    id: 'c4',
    title: 'Launch Challenge',
    description: 'Build excitement before a product launch with daily content.',
    typeId: 'challenge',
    category: 'Online Store',
    difficulty: 'Intermediate',
    bestForTag: 'Pre-launch buzz',
    stepCount: 4,
    strategyNote: 'Reveal the product on the final day.',
  },
  {
    id: 'c5',
    title: 'Accountability Sprint',
    description: 'Small group sprint with daily check-ins and a final offer.',
    typeId: 'challenge',
    category: 'Services',
    difficulty: 'Beginner',
    bestForTag: 'Easy to run',
    stepCount: 3,
    strategyNote: 'Keep the group small for better engagement.',
  },
]

export const INLINE_TIPS = [
  'Keep it simple! Your first page should only do one thing — get people interested.',
  "Don't overthink it. A simple 2-page setup is usually all you need to get started.",
  'Always click through your own pages to make sure everything works before showing your friends.',
  'The big bold text at the top of your page is super important. Make sure it sounds exciting!',
]
